/** POST-MATCH LEVEL-UP CARD.
 *
 *  The server banks a match's XP and sends {xp, total?} (ServerMsg.XpAward).
 *  With `total` (lifetime XP after banking) present, this renders a real
 *  progression moment: the rank badge, the current level, an animated bar
 *  filling toward the next level, and — when you crossed a level or a rank
 *  boundary — a LEVEL UP / RANK UP banner with a rising chime. Without `total`
 *  (the DB seed hadn't landed) it falls back to the old flat "+N XP" card, so
 *  the feedback never regresses.
 *
 *  Pure display: all level/rank math comes from the shared `summarizeXpAward`,
 *  the same function `agent/bench-progression-card.mjs` asserts against, so the
 *  card can never disagree with the curve. */
import { summarizeXpAward } from "@neon-purge/shared";
import { el } from "./dom.js";
import { audio } from "../game/audio.js";

/** Show the post-match XP card. `total` omitted → plain "+N XP" fallback. */
export function showXpCard(xp: number, total?: number): void {
  if (!(xp > 0)) return;

  // No lifetime total → the old flat card, unchanged.
  if (typeof total !== "number") {
    const flat = el("div", { class: "xp-card" },
      el("div", { class: "xp-amount", text: `+${Math.round(xp)} XP` }),
      el("div", { class: "xp-sub", text: "MATCH COMPLETE" }),
    );
    document.body.append(flat);
    window.setTimeout(() => flat.remove(), 4200);
    return;
  }

  const s = summarizeXpAward(xp, total);
  const card = el("div", { class: `xp-card xp-card-rank${s.leveledUp ? " leveled" : ""}` });

  // headline: LEVEL UP! / RANK UP! celebration, else the neutral MATCH COMPLETE
  if (s.rankedUp) {
    card.append(el("div", { class: "xp-head rankup", text: `RANK UP · ${s.tier.name}` }));
  } else if (s.leveledUp) {
    card.append(el("div", { class: "xp-head", text: s.levelsGained > 1 ? `LEVEL UP! ×${s.levelsGained}` : "LEVEL UP!" }));
  } else {
    card.append(el("div", { class: "xp-sub", text: "MATCH COMPLETE" }));
  }

  card.append(el("div", { class: "xp-amount", text: `+${s.earned} XP` }));

  // rank badge + level row
  const badge = el("span", { class: "xp-rank-badge", text: s.tier.name });
  badge.style.color = s.tier.color;
  badge.style.borderColor = s.tier.color;
  const row = el("div", { class: "xp-rank-row" },
    badge,
    el("span", { class: "xp-level", text: `LEVEL ${s.level}` }),
  );
  card.append(row);

  // progress bar toward the next level — animate the fill up from where the
  // previous level's leftover sat (a level-up sweeps the bar over the boundary).
  const bar = el("div", { class: "xp-bar" });
  const fill = el("div", { class: "xp-bar-fill" });
  fill.style.background = s.tier.color;
  fill.style.color = s.tier.color; // currentColor drives the bar's glow
  // start at 0 for a fresh level, else the pre-earned fraction of THIS level
  const startFrac = s.leveledUp ? 0 : Math.max(0, Math.min(1, (s.into - s.earned) / Math.max(1, s.span)));
  fill.style.width = `${Math.round(startFrac * 100)}%`;
  bar.append(fill);
  card.append(bar);
  card.append(el("div", { class: "xp-bar-label", text: `${s.into} / ${s.span} XP` }));

  document.body.append(card);
  // next frame: run the CSS width transition to the real fraction
  requestAnimationFrame(() => { fill.style.width = `${Math.round(s.frac * 100)}%`; });

  if (s.rankedUp) audio.rankUp();
  else if (s.leveledUp) audio.levelUp();

  window.setTimeout(() => card.remove(), s.leveledUp ? 5200 : 4200);
}

/** Debug hook so a browser screenshot can capture the card without a live match.
 *  Enabled only when `localStorage.neonpurge.xpdemo` is set; exposes
 *  `window.__xpCardDemo(xp, total)`. */
export function installXpCardDemo(): void {
  try {
    if (!localStorage.getItem("neonpurge.xpdemo")) return;
  } catch { return; }
  (window as unknown as { __xpCardDemo?: (xp: number, total?: number) => void }).__xpCardDemo =
    (xp: number, total?: number) => showXpCard(xp, total);
}
